import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Button,
  Stack,
  Fade,
  alpha,
  Card,
  CardContent,
  useTheme,
} from '@mui/material';
import {
  TrendingDown,
  Info,
  FilterList,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

function DowntrendEmptyState({ onOpenIndicators, onEnableBatchMode }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const cardSx = {
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    borderRadius: 3,
    background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
    overflow: 'visible',
  };

  const contentSx = { p: { xs: 4, sm: 6, md: 8 }, textAlign: 'center' };

  const iconWrapSx = {
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    mx: 'auto',
    mb: 4,
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -10,
      right: -10,
      width: 40,
      height: 40,
      background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
      borderRadius: '50%',
      boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.4)}`,
    },
  };

  const titleSx = { mb: 2, letterSpacing: -0.5 };

  const messageSx = {
    mb: 4,
    maxWidth: 500,
    mx: 'auto',
    fontSize: '1rem',
    lineHeight: 1.7,
  };

  const primaryButtonSx = {
    px: 4,
    py: 1.5,
    borderRadius: 2,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
    '&:hover': {
      boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}`,
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.3s ease',
  };

  const secondaryButtonSx = {
    px: 4,
    py: 1.5,
    borderRadius: 2,
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    borderWidth: 2,
    '&:hover': {
      borderWidth: 2,
      transform: 'translateY(-2px)',
    },
    transition: 'all 0.3s ease',
  };

  return (
    <Fade in timeout={600}>
      <Card elevation={0} sx={cardSx}>
        <CardContent sx={contentSx}>
          <Box sx={iconWrapSx}>
            <TrendingDown sx={{ fontSize: 64, color: 'white', zIndex: 1 }} />
          </Box>
          <Typography variant="h4" fontWeight={700} sx={titleSx}>
            {t('downtrend.emptyState.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={messageSx}>
            {t('downtrend.emptyState.message')}
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<Info />}
              onClick={onOpenIndicators}
              sx={primaryButtonSx}
            >
              {t('downtrend.emptyState.viewIndicators')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<FilterList />}
              onClick={onEnableBatchMode}
              sx={secondaryButtonSx}
            >
              {t('downtrend.emptyState.batchAnalysis')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Fade>
  );
}

DowntrendEmptyState.propTypes = {
  onOpenIndicators: PropTypes.func.isRequired,
  onEnableBatchMode: PropTypes.func.isRequired,
};

export default DowntrendEmptyState;
